import React, { FC, useState } from 'react';

import { LucidTheme, mqMin, getColor } from '@lucid/theme';

import Box from '../box/box';
import Container from '../container/container';
import Heading from '../heading/heading';
import ImageWithPlaceholder from '../image/image-with-placeholder';
import Link from '../link/link';
import VideoPlayer from '../video-player/video-player';
import { ComponentLink } from '../../types/generics';
import VideoCarousel from '../video-carousel/video-carousel';

export interface VideoProps {
  /** The link to the video. */
  videoLink: string;
  /** The link for a fallback image for the video. */
  videoLinkPlaceholder?: string;
  /** The link alt text for the fallback image. */
  videoLinkPlaceholderAlt?: string;
  /** The accessible description for the video. */
  videoDescription?: string;
  /** The unique ID for the description element (must be a valid id attribute) */
  videoDescriptionElementId?: string;
}
export interface FeaturedVideoProps {
  /** Classname to apply to the component */
  className?: string;
  /** The CTA button link and text. */
  cta?: ComponentLink;
  /** The background color for the background shown behind the video. */
  background?: string;
  /** The list of bullet points for the FeaturedVideo component. */
  items: string[];
  /** The header for the FeaturedVideo component. */
  header: string;
  /** Option to change the text background color */
  textBackground?: string;
  /** The videos for the FeaturedVideo component */
  videos: VideoProps[];
}

const FeaturedVideo: FC<FeaturedVideoProps> = ({
  className,
  cta,
  background = 'transparent',
  items = [],
  header,
  textBackground,
  videos,
}) => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  return (
    <Box
      css={(theme: LucidTheme) => {
        const textBackgroundColor = getColor({ color: textBackground, theme });
        return [
          {
            background: `linear-gradient(${theme.colors.coolGrey[100]} 60%, ${background} 60%)`,
            [mqMin.tabletLandscape]: {
              background: `linear-gradient(90deg, ${theme.colors.coolGrey[100]} 47.5%, ${background} 47.5%)`,
            },
          },
          textBackground && {
            background: `linear-gradient(${textBackgroundColor} 60%, ${background} 60%)`,
            [mqMin.tabletLandscape]: {
              background: `linear-gradient(90deg, ${textBackgroundColor} 47.5%, ${background} 47.5%)`,
            },
          },
        ];
      }}
      // eslint-disable-next-line react/jsx-sort-props
      className={className}
      my={6}
      py={6}
    >
      <Container>
        <Box alignItems="center" display="flex" flexWrap="wrap" justifyContent="space-between">
          <Box
            css={{
              flex: '1 1 100%',
              [mqMin.tabletLandscape]: {
                flex: '0 1 35%',
              },
            }}
          >
            <Heading as="h2" color="white" mb="5" variant="headline2XL">
              {header}
            </Heading>
            <ul
              style={{
                margin: '0 0 2rem',
                marginBlockStart: 0,
                paddingInlineStart: 0,
                display: 'flex',
                flexWrap: 'wrap',
              }}
            >
              {items?.map((item, index) => (
                <li
                  key={`item-${index}`}
                  css={(theme: LucidTheme) => ({
                    visibility: index === currentVideoIndex || videos.length <= 1 ? 'visible' : 'hidden',
                    marginBottom: '1rem',
                    color: theme.colors.white,
                    flex: '0 1 100%',
                    [mqMin.tabletSmall]: {
                      flex: '0 1 50%',
                    },
                    [mqMin.tabletLandscape]: {
                      flex: '0 1 100%',
                    },
                  })}
                >
                  {item}
                </li>
              ))}
            </ul>
            {cta && cta.linkUrl && (
              <Box
                css={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Link displayAsButton href={cta.linkUrl} variant="secondary">
                  {cta.linkText}
                </Link>
              </Box>
            )}
          </Box>
          {videos && videos.length > 1 ? (
            <VideoCarousel
              animationTiming={600}
              parentVideoIndex={currentVideoIndex}
              setParentVideoIndex={setCurrentVideoIndex}
              variant="featuredVideo"
              videos={videos}
            />
          ) : (
            <Box
              css={{
                flex: '1 1 100%',
                [mqMin.tabletLandscape]: {
                  flex: '0 1 60%',
                },
              }}
            >
              <VideoPlayer
                controls
                placeholder={() => (
                  <ImageWithPlaceholder
                    alt={videos[0].videoLinkPlaceholderAlt || ''}
                    src={videos[0].videoLinkPlaceholder!}
                  />
                )}
                playing={false}
                url={videos[0].videoLink}
              />
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export { FeaturedVideo };
export default FeaturedVideo;
